import {
  ApplicationRef,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  QueryList,
  ViewChildren,
  Output,
  EventEmitter,
  Injector,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbModal, NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Observable, of, concat, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  mergeMap,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import Swal from 'sweetalert2';

import { DefaultRequestParamsConstant } from '@app/core/_constant/index.constants';
import { ProductTypeConstant } from '@app/core/_constant/product-type.constants';
import { SalesOrderStatusConstant } from '@app/core/_constant/sales-orders.constants';
import { InventoryService } from '@app/core/inventory/services/inventory.service';
import { LocationService } from '@app/core/locations/services/location.service';
import { RfqService } from '@app/core/quotes';
import { SalesService } from '@app/core/sales/services/sales.service';
import { SettingsService } from '@app/core/settings/services/settings.service';
import { EFeatureFlag } from '@app/feature-toggle-list';
import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';

import { AdjustmentEntryStockLocationsFormComponent } from '../adjustment-entry-stock-locations-form/adjustment-entry-stock-locations-form.component';

@Component({
  selector: 'cnc-adjustment-entry-form',
  templateUrl: './adjustment-entry-form.component.html',
  styleUrls: ['./adjustment-entry-form.component.scss'],
})
export class AdjustmentEntryFormComponent implements OnInit {
  @ViewChildren('stockWarehousesSelect')
  stockWarehousesSelect :QueryList<ElementRef>;

  loading :boolean;

  rfq$ :Observable<any[]>;
  rfqLoading$ :Observable<boolean>;
  rfqInput$ = new Subject<string>();
  keywordInput$ = new Subject<string>();
  private onDestroy$ = new Subject();

  adjustmentEntries :any[] = [];
  adjustmentRemarks :string = '';

  rfqEntries :any[] = [];
  unitOfMeasures :any[] = [];

  warehousesRaw :any[] = [];
  warehouses :any[] = [];
  locations :any[] = [];
  @Input() selected_location :any; // from inventory sku
  @Input() rfq :any;
  @Input() isModal = false;
  activeModal :any;
  currentRfq :any;
  hasChanges :boolean;

  // feature toggle
  ftLocationBins :boolean = false;
  ftLotTracking :boolean = false;

  location$ :Observable<any[]>;
  locationLoading :boolean = false;
  locationInput$ :Subject<string> = new Subject<string>();

  salesOrders$ :Observable<any[]>;
  salesOrderLoading :boolean = false;
  orderNumberInput$ :Subject<string> = new Subject<string>();
  selectedSalesOrder = null;
  salesOrderAdjusmentEntries = [];

  selectedLocation = null;
  mode :'sku' | 'location' | 'keyword' | 'sales-order' = 'sku';
  locationSkus :any[] = [];
  loadingLocationSkus :boolean = false;

  @Output() inventoryStockLocationUpdate :EventEmitter<any> = new EventEmitter();

  constructor(
    private injector :Injector,
    public rfqSvc :RfqService,
    public invSvc :InventoryService,
    private toastr :ToastrService,
    private locationSvc :LocationService,
    private cd :ChangeDetectorRef,
    private modal :NgbModal,
    private ft :FeatureToggleService,
    private settingsService :SettingsService,
    private activatedRoute :ActivatedRoute,
    private salesOrderService :SalesService,
  ) {
    this.ftLotTracking = this.ft.featureEnabled(EFeatureFlag.LOT_TRACKING);

    this.settingsService
      .getKeyValue('track_inventory_by_location')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((settingEnabled :boolean) => {
        this.ftLocationBins = settingEnabled;
        this.cd.detectChanges();
      });

    this.activatedRoute.queryParams
      .pipe(
        mergeMap((params) => {
          if (params.location) {
            return this.locationSvc.getById(params.location);
          }

          return of(null);
        }),
        takeUntil(this.onDestroy$),
      )
      .subscribe((location :any) => {
        if (location?.id) {
          this.mode = 'location';
          this.locationInput$.next(location.location_name);
          this.updateLocation(location);
          this.cd.detectChanges();
        }
      });
  }

  ngOnInit() {
    this.loadLocations();
    this.loadSalesOrders();
    this.locationSvc
      .getAll({ all: 1, parent_id: 0 })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((resp) => {
        this.warehousesRaw = resp.filter(
          (row :{ parent_id :number }) => row.parent_id === 0,
        );
        this.warehousesRaw.splice(0, 0, { id: 0, location_name: 'GENERAL' });
        this.warehouses = this.warehousesRaw.filter((w) => w.id > 0);
        if (this.isModal) {
          this.invSvc
            .get({ skuAdjustmentSelectedId: this.rfq.id })
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((res :any) => {
              if (res && res.data && res.data.length > 0) {
                this.rfq = res.data[0];
                this.setRfq();
              }
            });
        }
      });
    if (this.isModal) {
      this.activeModal = <NgbActiveModal>this.injector.get(NgbActiveModal);
      this.currentRfq = this.rfq;
    }

    this.rfqSvc.setRfqs([]);
    this.getDataFromService();
    this.onInputRfqUpdated().subscribe();
    this.onKeywordInputUpdated().subscribe();
  }

  private getDataFromService() :void {
    this.rfq$ = this.rfqSvc.rfqs$;
    this.rfqLoading$ = this.rfqSvc.loadingRfq$;
  }

  private onInputRfqUpdated() :Observable<string> {
    return this.rfqInput$.asObservable().pipe(
      debounceTime(500), // 0.5 sec delay
      distinctUntilChanged(),
      tap((name :string) => {
        if (name && name !== '') {
          this.rfqSvc.getV2Api({
            sku: name,
            product_type: ProductTypeConstant.PRODUCT,
            product_type_id: 2,
            limit: 100,
          });
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }
  private onKeywordInputUpdated() :Observable<string> {
    return this.keywordInput$.asObservable().pipe(
      debounceTime(500), // 0.5 sec delay
      distinctUntilChanged(),
      tap((name :string) => {
        if (name && name !== '') {
          this.rfqSvc.getV2Api({
            keyword: name,
            product_type: ProductTypeConstant.PRODUCT,
            product_type_id: 2,
            limit: 100,
          });
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  public updateSalesOrder(salesOrder) {
    this.salesOrderAdjusmentEntries = [];
    this.salesOrderLoading = true;
    this.selectedSalesOrder = salesOrder;
    this.selectedLocation = null;

    this.cd.detectChanges();
    this.salesOrderService.getSalesOrderStockLogs(salesOrder.id).subscribe(
      (stockLogs) => {
        if (stockLogs.length) {
          if (salesOrder.sales_items.length) {
            this.composerSalesOrderAdjustmentEntriesFromSalesItems(
              stockLogs,
              salesOrder.sales_items,
            );
          } else {
            this.composerSalesOrderAdjustmentEntriesFromOrderItems(
              stockLogs,
              salesOrder.order_items,
            );
          }
        }
        this.salesOrderLoading = false;
        this.cd.detectChanges();
      },
      (_) => {
        this.salesOrderLoading = false;
        this.cd.detectChanges();
      },
    );
  }

  private composerSalesOrderAdjustmentEntriesFromOrderItems(
    stockLogs :any[],
    orderItems :any[],
  ) {
    orderItems.forEach((orderItem) => {
      if (orderItem.ignore_item) {
        return;
      }

      if (orderItem.rfq.kit_items?.length) {
        const kitOrderItems = [];
        orderItem.rfq.kit_items.forEach((kitItem) => {
          kitOrderItems.push({
            rfq_id: kitItem.product_id,
            rfq: kitItem.product,
            stock: kitItem.product.stock,
            ordered_qty: kitItem.base_uom_quantity * orderItem.ordered_qty,
            base_uom_id: kitItem.product.default_selling_unit_id,
            base_uom: kitItem.product.default_selling_unit,
          });
        });
        this.composerSalesOrderAdjustmentEntriesFromOrderItems(
          stockLogs,
          kitOrderItems,
        );

        return;
      }

      const itemStockLog = stockLogs.find(
        (stockLog) => stockLog.stock.rfq_id === orderItem.rfq_id,
      );
      const pickupLocationStockLog = stockLogs.find((stockLog) => {
        return stockLog.stock.rfq_id === orderItem.rfq_id && stockLog.location;
      });

      if (!itemStockLog || !pickupLocationStockLog) {
        return;
      }

      const itemEntry = {
        rfqId: orderItem.rfq_id,
        sku: orderItem.rfq.internal_sku,
        name: orderItem.rfq.product_name,
        warehouseId: orderItem.stock.warehouse_id,
        warehouse: orderItem.stock.warehouse,
        locationId: itemStockLog.location_id,
        location: pickupLocationStockLog.location,
        lotId: itemStockLog?.lot_id,
        lot: itemStockLog.rfq_lot,
        currentQuantity: orderItem.stock.qty_onhand,
        quantity: orderItem.ordered_qty,
        adjustmentQuantity: orderItem.ordered_qty,
        baseUomId: orderItem.base_uom_id,
        baseUom: orderItem.base_uom?.name,
        stockId: itemStockLog.stock_id,
        stock: itemStockLog.stock,
      };

      this.salesOrderAdjusmentEntries.push(itemEntry);
    });
  }

  private composerSalesOrderAdjustmentEntriesFromSalesItems(
    stockLogs :any[],
    salesItems :any[],
  ) {
    salesItems.forEach((salesItem) => {
      const salesItemStockLog = stockLogs.find(
        (stockLog) => stockLog.stock.rfq_id === salesItem.rfqId,
      );
      const pickupLocationStockLog = stockLogs.find((stockLog) => {
        return stockLog.stock.rfq_id === salesItem.rfqId && stockLog.location;
      });

      if (!salesItemStockLog || !pickupLocationStockLog) {
        return;
      }

      const itemEntry = {
        rfqId: salesItem.rfqId,
        sku: salesItem.sku,
        name: salesItem.name,
        warehouseId: salesItem.stock.warehouse_id,
        warehouse: salesItem.stock.warehouse,
        locationId: salesItemStockLog.location_id,
        location: pickupLocationStockLog.location,
        lotId: salesItemStockLog?.lot_id,
        lot: salesItemStockLog.rfq_lot,
        currentQuantity: salesItem.stock.qty_onhand,
        quantity: salesItem.quantity,
        adjustmentQuantity: salesItem.quantity,
        baseUomId: salesItem.baseUomId,
        baseUom: salesItem.base_uom?.name,
        stockId: salesItemStockLog.stock_id,
        stock: salesItemStockLog.stock,
      };
      this.salesOrderAdjusmentEntries.push(itemEntry);
    });
  }

  public addSalesOrderAdjustment() {
    this.salesOrderAdjusmentEntries.forEach((adjustmentEntry) => {
      const stock_locations = [
        {
          ...adjustmentEntry.location,
          adjusted_uom_id: adjustmentEntry.baseUomId,
          adjustmentQuantity: adjustmentEntry.adjustmentQuantity,
          pivot: {
            lot: adjustmentEntry.lot,
            lot_id: adjustmentEntry.lotId,
            location_id: adjustmentEntry.locationId,
            onhand_quantity: adjustmentEntry.currentQuantity,
            reserved_quantity: 0,
            rfq_stock_id: adjustmentEntry.stockId,
          },
        },
      ];
      const entry = {
        ...adjustmentEntry.stock,
        stock_locations: stock_locations,
        rfq_id: adjustmentEntry.rfqId,
        product_name: adjustmentEntry.name,
        sku: adjustmentEntry.sku,
      };
      this.adjustmentEntries.push(entry);
    });
    this.salesOrderAdjusmentEntries = [];
    this.selectedSalesOrder = null;
    this.cd.detectChanges();
  }

  private loadSalesOrders() {
    this.salesOrders$ = concat(
      of([]), // default items
      this.orderNumberInput$.pipe(
        debounceTime(250),
        distinctUntilChanged(),
        tap(() => (this.salesOrderLoading = true)),
        switchMap((term) =>
          this.salesOrderService
            .getSalesOrders(
              {
                orderNo: term,
                posted: true,
                includeDeleted: true,
                orderStatus: [
                  SalesOrderStatusConstant.SHIPPED,
                  SalesOrderStatusConstant.CANCELLED,
                ].join(','),
                per_page: DefaultRequestParamsConstant.limit,
                page: DefaultRequestParamsConstant.page,
              },
              [
                'orderItems.rfq.kitItems.product.stock.warehouse',
                'orderItems.rfq.kitItems.product.defaultSellingUnit',
                'orderItems.baseUom',
                'orderItems.stock.warehouse',

                'salesItems.rfq',
                'salesItems.baseUom',
                'salesItems.stock.warehouse',
              ].join('|'),
              true,
            )
            .pipe(
              catchError(() => of([])), // empty list on error
              tap(() => (this.salesOrderLoading = false)),
              map((resp :any) => {
                return resp.data;
              }),
              takeUntil(this.onDestroy$),
            ),
        ),
      ),
    );
  }

  public updateLocation(location) {
    this.selectedLocation = location;
    this.selectedSalesOrder = null;
    if (location) {
      this.loadingLocationSkus = true;
      this.cd.detectChanges();
      this.invSvc
        .get({ location: location.id, per_page: 1000 })
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(({ data } :{ data :any[] }) => {
          this.loadingLocationSkus = false;
          this.locationSkus = data.map((sku) => {
            if (sku?.unit_of_measures.length <= 0) {
              sku.unit_of_measures = sku.parent?.unit_of_measures;
            }

            sku.location_stocks.forEach((location_stock :any) => {
              if (location_stock && location_stock.stock_locations) {
                location_stock.stock_locations.forEach((stock_location :any) => {
                  const baseUnit :any = sku?.unit_of_measures?.find(
                    (uom) => uom.base_unit === 1,
                  );

                  if (stock_location) {
                    stock_location.adjusted_uom_id = baseUnit?.id;
                  }
                });
              }
            });

            this.unitOfMeasures[sku.id] = sku?.unit_of_measures;

            return sku;
          });
          this.cd.detectChanges();
        });
    }
  }

  private loadLocations() {
    this.location$ = concat(
      of([]), // default items
      this.locationInput$.pipe(
        distinctUntilChanged(),
        tap(() => (this.locationLoading = true)),
        switchMap((term) =>
          this.locationSvc
            .getAsync({
              keyword: term,
              per_page: DefaultRequestParamsConstant.limit,
              page: DefaultRequestParamsConstant.page,
              withoutStockLocations: true,
            })
            .pipe(
              catchError(() => of([])), // empty list on error
              tap(() => (this.locationLoading = false)),
              map((resp :any) => resp.data),
            ),
        ),
        takeUntil(this.onDestroy$),
      ),
    );
  }

  private rollbackEntryWarehouse(entry) {
    entry.stock.warehouse_id = entry.originalWarehouseId;
    entry.stock.warehouse = this.warehouses.find(
      (row :any) => row.id == entry.stock.warehouse_id,
    );
    this.cd.detectChanges();
  }

  newCreateStockEntry(warehouseId) {
    const warehouse = this.warehouses.find((w) => w.id === warehouseId);
    this.invSvc
      .createStockEntry({
        warehouse_id: warehouseId,
        rfq_id: this.rfq.id,
      })
      .subscribe((res) => {
        this.toastr.success('Stock warehouse updated.', 'Success');
        this.rfq.location_stocks.push({
          ...res,
          warehouse_id: warehouseId,
          warehouse,
        });
        this.inventoryStockLocationUpdate.emit(true);
        this.cd.detectChanges();
      });
  }

  private createStockEntry(entry :any) {
    const payload :any = {
      warehouse_id: entry.stock.warehouse_id,
      rfq_id: entry.rfq.id,
    };
    this.invSvc.createStockEntry(payload).subscribe(
      (resp :any) => {
        this.toastr.success('Stock warehouse updated.', 'Success');
        entry.stock = resp;
        entry.originalWarehouseId = entry.stock.warehouse_id;
        this.cd.detectChanges();
      },
      (error) => {
        this.toastr.error('Update error. Please try again.', 'Error');
      },
    );
  }

  stockEntryList() :any[] {
    if (!this.ftLocationBins) {
      return [];
    }

    return this.warehouses.filter((w) => {
      return !this.rfq?.location_stocks.find((ls) => {
        return ls.warehouse && ls.warehouse.id === w.id;
      });
    });
  }

  stockEntryListOmit(warehouseId) :any[] {
    if (!this.ftLocationBins) {
      return [];
    }

    return this.warehouses.filter((w) => {
      return (
        w.id &&
        warehouseId !== w.id &&
        !this.rfq?.location_stocks.find((ls) => {
          return ls.warehouse && ls.warehouse.id === w.id;
        })
      );
    });
  }

  newStockWarehouseChange(entry, warehouseId) {
    Swal.fire({
      title: 'Update stock warehouse?',
      text: 'This will move all stocks to this warehouse?',
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Transfer Stock Warehouse',
    }).then((result) => {
      if (result.value) {
        const payload :any = {
          warehouse_id: warehouseId,
        };
        this.invSvc.updateStockWarehouse(entry.id, payload).subscribe(
          (resp :any) => {
            this.toastr.success('Stock warehouse updated.', 'Success');
            entry.warehouse_id = warehouseId;
            entry.warehouse = this.warehouses.find((w) => w.id === warehouseId);
            this.inventoryStockLocationUpdate.emit(true);
            this.cd.detectChanges();
          },
          (error) => {
            this.toastr.error('Update error. Please try again.', 'Error');
          },
        );
      } else {
        this.rollbackEntryWarehouse(entry);
      }
    });
  }

  stockWarehouseChange(entry :any, idx :number) {
    if (!entry.originalWarehouseId) {
      const whIndex :number = this.rfqEntries.findIndex(
        (row :any) => row.stock.warehouse_id == entry.stock.warehouse_id,
      );
      if (whIndex >= 0 && idx !== whIndex) {
        Swal.fire(
          'Stock entry exist',
          'A stock entry for this warehouse already exist. You can update quantities from there.',
          'warning',
        );
        entry.stock.warehouse_id = null;

        return;
      }
      this.createStockEntry(entry);

      return;
    }

    if (entry.stock.warehouse_id != entry.originalWarehouseId) {
      const extWarehouseidx :number = this.rfqEntries.findIndex(
        (row :any) => row.originalWarehouseId == entry.stock.warehouse_id,
      );
      if (extWarehouseidx >= 0) {
        Swal.fire(
          'Error',
          'Duplicate stock warehouse entry. Rolling back to original warehouse.',
          'error',
        );
        this.rollbackEntryWarehouse(entry);

        return;
      }
      Swal.fire({
        title: 'Update stock warehouse?',
        text: 'This will move all stocks to this warehouse?',
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Transfer Stock Warehouse',
      }).then((result) => {
        if (result.value) {
          const payload :any = {
            warehouse_id: entry.stock.warehouse_id,
          };
          this.invSvc.updateStockWarehouse(entry.stock.id, payload).subscribe(
            (resp :any) => {
              this.toastr.success('Stock warehouse updated.', 'Success');
              entry.originalWarehouseId = entry.stock.warehouse_id;
            },
            (error) => {
              this.toastr.error('Update error. Please try again.', 'Error');
            },
          );
        } else {
          this.rollbackEntryWarehouse(entry);
        }
      });
    }
  }

  newAddLocation(warehouseId) {
    const modalRef = this.modal.open(
      AdjustmentEntryStockLocationsFormComponent,
      {},
    );
    const entry = this.rfq.location_stocks.find(
      (ls) => ls.warehouse_id === warehouseId,
    );
    modalRef.componentInstance.stock = entry;
    modalRef.result.then(
      (stock) => {
        if (stock && stock.stock_locations) {
          stock.stock_locations.forEach((stock_location :any) => {
            const baseUnit :any = this.rfq.unit_of_measures.find(
              (uom) => uom.base_unit === 1,
            );

            if (stock_location) {
              stock_location.adjusted_uom_id = baseUnit?.id;
            }
          });

          entry.stock_locations = stock.stock_locations;
        }
        this.inventoryStockLocationUpdate.emit(true);
        this.cd.detectChanges();
      },
      () => {},
    );
  }

  getListOfAvailableWarehousesByEntry() {}

  addStockLocation(entry :any) {
    if (!entry.stock.warehouse) {
      entry.stock.warehouse = this.warehouses.find(
        (row :any) => row.id === entry.stock.warehouse_id,
      );
      if (!entry.stock.warehouse) {
        Swal.fire('Error', 'Please set a warehouse first.', 'error');

        return;
      }
    }
    const modalRef = this.modal.open(
      AdjustmentEntryStockLocationsFormComponent,
      {},
    );
    modalRef.componentInstance.stock = entry.stock;
    modalRef.result.then(
      (stock) => {
        if (stock && stock.stock_locations) {
          entry.stock.stock_locations = stock.stock_locations;
        }
        this.cd.detectChanges();
      },
      () => {},
    );
  }

  newDeleteLocation(entry, locationId, lotId :number | null) {
    this.invSvc.removeStockLocationLot(entry.id, locationId, lotId).subscribe(
      (resp :any) => {
        this.toastr.success('Location removed.', 'Success');
        entry.stock_locations = entry.stock_locations.filter(
          (l) => l.id !== locationId,
        );
        this.hasChanges = true;
        this.inventoryStockLocationUpdate.emit(true);
        this.cd.detectChanges();
      },
      (error) => {
        this.toastr.error('Remove error. Please try again.', 'Error');
      },
    );
  }

  deleteRfqLocation(
    row :any,
    locationId :number,
    idx :number,
    lotId :number | null,
  ) :void {
    const stockId :number = row.stock
      ? row.stock.id
      : row.stock_id
        ? row.stock_id
        : null;
    const stock :any = row.stock ? row.stock : row;
    if (!stockId || !stock) {
      return;
    }

    this.invSvc.removeStockLocationLot(stockId, locationId, lotId).subscribe(
      (resp :any) => {
        this.toastr.success('Location removed.', 'Success');
        stock.stock_locations.splice(idx, 1);
        this.cd.detectChanges();
      },
      (error) => {
        this.toastr.error('Remove error. Please try again.', 'Error');
      },
    );
  }

  filterLocations(locations :any[], warehouseId :string) {
    return (
      locations?.filter(
        (location) => location.warehouse_id === parseInt(warehouseId, 10),
      ) || []
    );
  }

  filterValidLocations(locations :any[]) :any[] {
    return locations.filter((location) => location.warehouse);
  }

  newSetRfq() {
    if (!this.rfq) {
      return;
    }
    this.selectedLocation = null;
    const toMerge :any[] = [];
    if (this.rfq.location_stocks && this.rfq.location_stocks.length > 0) {
      this.rfq.location_stocks.forEach((stock :any) => {
        const exist :any = this.rfq.location_stocks.find(
          (ls) => ls.warehouse_id === stock.warehouse_id,
        );

        stock.stock_locations.forEach((stock_location :any) => {
          const baseUnit :any = this.rfq.unit_of_measures.find(
            (uom) => uom.base_unit === 1,
          );

          if (stock_location) {
            stock_location.adjusted_uom_id = baseUnit?.id;
          }
        });

        if (exist) {
          toMerge.push({
            from_stock_id: stock.id,
            to_stock_id: exist.id,
          });
        }
      });
    }
    // toMerge.forEach((value, id) => {
    //   this.invSvc.mergeStocks(value).subscribe((resp) => {
    //     if (resp && resp.id) {
    //       const idx: any = this.rfq.location_stocks.findIndex(
    //         (ls) => ls.id === resp.id,
    //       );
    //       if (idx || idx >= 0) {
    //         this.rfqEntries[idx].stock = resp;
    //       }
    //     }
    //   });
    // });
    this.cd.detectChanges();
  }

  setRfq() {
    this.rfqEntries = [];
    const toMerge :any[] = [];
    if (this.rfq.location_stocks && this.rfq.location_stocks.length > 0) {
      this.rfq.location_stocks.forEach((stock :any) => {
        // if (this.selected_location.location_id == stock.location_id) {
        const exist :any = this.rfqEntries.find(
          (row) => row.stock && row.stock.warehouse_id == stock.warehouse_id,
        );
        if (exist && exist.stock) {
          toMerge.push({
            from_stock_id: stock.id,
            to_stock_id: exist.stock.id,
          });
        } else {
          if (stock.qty_incoming < 0) {
            stock.qty_incoming = 0;
          }
          if (stock.qty_pending < 0) {
            stock.qty_pending = 0;
          }
          this.rfqEntries.push({
            rfq: this.rfq,
            stock: stock,
            originalWarehouseId: stock.warehouse_id,
          });
        }
        // }
      });
    } else {
      this.rfqEntries.push({
        rfq: this.rfq,
        stock: {},
      });
    }
    toMerge.forEach((value, id) => {
      this.invSvc.mergeStocks(value).subscribe((resp) => {
        if (resp && resp.id) {
          const idx :any = this.rfqEntries.findIndex(
            (row) => row.stock && row.stock.id == resp.id,
          );
          if (idx || idx >= 0) {
            this.rfqEntries[idx].stock = resp;
          }
        }
      });
    });

    const generalIdx :number = this.rfqEntries.findIndex(
      (row) => row.stock && row.stock.warehouse_id == 0,
    );
    if (generalIdx < 0) {
      this.warehouses = this.warehousesRaw.filter((row) => row.id > 0);
    } else {
      this.warehouses = this.warehousesRaw;
    }

    //FIXED ISSUE ON MODAL ADJUSTMENT ENTRY UOM NOT SHOWING
    this.rfqEntries.forEach((stock :any) => {
      if (stock.rfq.unit_of_measures.length <= 0) {
        stock.rfq.unit_of_measures = stock.rfq.parent.unit_of_measures;
      }

      if (stock.stock && stock.stock.stock_locations) {
        stock.stock.stock_locations.forEach((stock_location :any) => {
          const baseUnit :any = this.rfq.unit_of_measures.find(
            (uom) => uom.base_unit === 1,
          );

          if (stock_location) {
            stock_location.adjusted_uom_id = baseUnit?.id;
          }
        });
      }

      this.unitOfMeasures[stock.rfq.id] = stock.rfq?.unit_of_measures;
    });

    this.cd.detectChanges();
  }

  addNewRow() {
    this.rfqEntries.push({
      rfq: this.rfq,
      stock: {},
    });
  }

  getWarehouseName(wareHouseId :number) :any {
    return this.warehouses.find((w) => w.id === wareHouseId);
  }

  newAddAdjustments() {
    let hasLocations :boolean = false;
    this.rfq.location_stocks.forEach((ls) => {
      ls.stock_locations = ls?.stock_locations?.filter((l) => {
        return l?.adjustmentQuantity;
      });
      if (ls?.stock_locations?.length) {
        hasLocations = true;
        this.adjustmentEntries.push({
          ...ls,
          rfq_id: this.rfq.id,
          product_name: this.rfq.product_name,
          sku: this.rfq.internal_sku,
        });
      }
    });
    this.unitOfMeasures[this.rfq.id] = this.rfq?.unit_of_measures;

    if (!this.isModal && hasLocations) {
      this.rfq = undefined;
    } else if (!hasLocations) {
      Swal.fire(
        'No changes!',
        'No quantity changes have been made. No entry will be added to adjustment.',
        'warning',
      );
    }
  }

  addAdjustments() {
    let rowCount :number = 0;
    this.rfqEntries.forEach((entry :any) => {
      if (
        entry.stock.id &&
        (entry.stock.adj_incoming ||
          entry.stock.adj_onhand ||
          entry.stock.adj_pending)
      ) {
        rowCount += 1;
        this.adjustmentEntries.push({
          rfq_id: entry.rfq.id,
          stock_id: entry.stock.id,
          product_name: entry.rfq.product_name,
          sku: entry.rfq.internal_sku,
          warehouse_id: entry.stock.warehouse_id,
          location_id: entry.stock.location_id || 0,
          adj_incoming: entry.stock.adj_incoming || 0,
          adj_onhand: entry.stock.adj_onhand || 0,
          adj_pending: entry.stock.adj_pending || 0,
          adjusted_uom_id: entry.stock.adjusted_uom_id ?? null,
          stock_locations: entry.stock.stock_locations,
        });

        this.unitOfMeasures[entry.rfq.id] = entry.rfq?.unit_of_measures;
      }
    });
    if (rowCount == 0) {
      Swal.fire(
        'No changes!',
        'No quantity changes have been made. No entry will be added to adjustment.',
        'warning',
      );

      return;
    }
    this.rfq = undefined;
    this.rfqEntries = [];
  }

  addLocationSkuAdjustment() {
    let hasLocations :boolean = false;
    this.locationSkus.forEach((sku) => {
      sku.location_stocks.forEach((ls) => {
        ls.stock_locations = ls.stock_locations.filter((l) => {
          return l?.adjustmentQuantity;
        });
        if (ls.stock_locations.length) {
          hasLocations = true;
          this.adjustmentEntries.push({
            ...ls,
            rfq_id: sku.id,
            product_name: sku.product_name,
            sku: sku.internal_sku,
          });
        }

        this.unitOfMeasures[sku.id] = sku.unit_of_measures;
      });
    });
    this.selectedLocation = null;
    this.cd.detectChanges();

    if (!this.isModal && hasLocations) {
      this.rfq = undefined;
    } else if (!hasLocations) {
      Swal.fire(
        'No changes!',
        'No quantity changes have been made. No entry will be added to adjustment.',
        'warning',
      );
    }
  }

  postAdjustments() {
    //Start prevent double click
    this.loading = true;
    if (this.adjustmentEntries.length == 0) {
      Swal.fire(
        'Form Error!',
        'Please specify at least one SKU to adjust quantities.',
        'error',
      );

      return;
    }
    Swal.fire({
      title: 'Post adjustment entries?',
      text: 'Once posted, quantities will be updated and you cannot roll back this transaction?',
      type: 'info',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Post Adjustment Entries',
      allowOutsideClick: false,
    }).then((result) => {
      if (result.value) {
        let internalSku :string = '';
        const adjustments :any[] = [];
        this.adjustmentEntries.forEach((entry :any) => {
          internalSku = entry.sku;
          if (this.ftLocationBins) {
            entry.stock_locations.forEach((l) => {
              adjustments.push({
                rfq_id: entry.rfq_id,
                sku: entry.sku,
                warehouse_id: entry.warehouse_id,
                location_id: l.id,
                qty_onhand: l.adjustmentQuantity,
                adjusted_uom_id: l.adjusted_uom_id,
                lot_id: l.pivot?.lot_id ?? 0,
                // qty_pending: entry.adj_pending || 0,
                // qty_incoming: entry.adj_incoming || 0,
              });
            });
          } else {
            adjustments.push({
              rfq_id: entry.rfq_id,
              sku: entry.sku,
              warehouse_id: entry.warehouse_id,
              location_id: entry.location_id || 0,
              qty_onhand: entry.adj_onhand || 0,
              qty_pending: entry.adj_pending || 0,
              qty_incoming: entry.adj_incoming || 0,
              adjusted_uom_id: entry.adjusted_uom_id ?? null,
            });
          }
        });
        const payload = {
          remarks: this.adjustmentRemarks,
          adjustment_entries: adjustments,
          item_count: adjustments.length,
        };
        this.invSvc.postAdjustments(payload).subscribe(
          (resp :any) => {
            this.toastr.success('Posted adjustments.', 'Success');
            this.loading = false;
            this.reset();
            this.cd.detectChanges();
            if (this.isModal) {
              this.activeModal.close(resp);
            } else {
              this.rfqSvc.getV2Api({
                sku: internalSku,
                product_type: ProductTypeConstant.PRODUCT,
                product_type_id: 2,
                limit: 100,
              });
            }
          },
          (_) => {
            this.toastr.error('Post error. Please try again.', 'Success');
            this.loading = false;
          },
        );
      } else {
        this.loading = false;
      }
    });
  }
  trackByFn(item :any) {
    return item.id;
  }

  newRemoveEntry(entry) {
    Swal.fire({
      title: 'Remove line entry?',
      text: 'Once removed, data will be deleted and you cannot roll back this transaction.',
      type: 'info',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Remove',
    }).then((result) => {
      if (result.value) {
        this.rfqSvc
          .removeSkuProductVariantStockEntry(entry.id)
          .subscribe((resp :any) => {
            this.rfq.location_stocks = this.rfq.location_stocks.filter(
              (ls) => ls.id !== entry.id,
            );
            if (this.isModal) this.activeModal.close(resp.record);
            this.cd.detectChanges();
            this.toastr.success('Successfully deleted line entry.', 'Success');
          });
      }
    });
  }

  removeSkuProductVariantStockEntry(stock :any, lineItemNumber :number) :void {
    if (!stock || !stock.id) {
      this.rfqEntries.splice(lineItemNumber, 1);

      return;
    }
    Swal.fire({
      title: 'Remove line entry?',
      text: 'Once removed, data will be deleted and you cannot roll back this transaction.',
      type: 'info',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Remove',
    }).then((result) => {
      if (result.value) {
        this.rfqSvc
          .removeSkuProductVariantStockEntry(stock.id)
          .subscribe((resp :any) => {
            this.rfqEntries.splice(lineItemNumber, 1);
            if (this.isModal) this.activeModal.close(resp.record);
            this.cd.detectChanges();
            this.toastr.success('Successfully deleted line entry.', 'Success');
          });
      }
    });
  }

  closeModal() {
    if (!this.isModal) {
      return;
    }
    if (this.hasChanges) {
      this.activeModal.close(this.rfq);

      return;
    }
    this.activeModal.dismiss('');
  }

  reset() {
    this.rfqEntries = [];
    this.adjustmentEntries = [];
    this.adjustmentRemarks = '';
    this.rfq = undefined;
    this.currentRfq = undefined;
  }

  getUomById(rfq_id :number, uom_id :number) :string {
    return this.unitOfMeasures[rfq_id]?.find((unit :any) => unit.id === uom_id)
      ?.name;
  }

  getBaseUom(unitOfMeasures :any) :string {
    return unitOfMeasures?.find((uom :any) => uom.base_unit === 1)?.name;
  }

  getLot(lots :any, lot_id ?:number) :string {
    return lots.find((lot :any) => lot.id === lot_id)?.lot_number;
  }
}
